"use client";

import Link from "next/link";
import { useState } from "react";

import {
  AUTONOMY_PRACTITIONER_NPIS,
  buildTeReasonContext,
  generateTeMessage,
  generateTeReasonText,
  getMissingInformationSummary,
  hasAutonomyDiagnosisRules,
  hasSleepDiagnosis,
  isOptumServeReferral,
  requiresHmoReferral,
} from "@/configuration/groups/autonomy/configuration";
import { AUTONOMY_REFERRAL_MAPPING } from "@/configuration/groups/autonomy/document-mappings";
import { AUTONOMY_PRACTITIONER_MAPPINGS } from "@/configuration/groups/autonomy/generated/practitioner-mappings";
import { formatAutonomyRenderingMismatch } from "@/configuration/groups/autonomy/referral-chart-enrichment";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { PDFViewer } from "@/src/components/pdf-viewer/pdf-viewer";
import type {
  AutonomyReferralAssignmentFact,
  AutonomyReferralChartResult,
  AutonomyReferralEmailFact,
  AutonomyReferralEnrichmentFact,
  AutonomyReferralInstructionsPresentation,
  AutonomyReferralOtherReferralOutcome,
  AutonomyReferralTelephoneEncounterDraftFact,
  AutonomyReferralTelephoneEncounterFact,
  AutonomyReferralUploadFact,
} from "@/src/components/server/autonomy-referral-instructions-step-output";
import { Alert, AlertDescription } from "@/src/components/shadcn/ui/alert";
import { Button } from "@/src/components/shadcn/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/src/components/shadcn/ui/dialog";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { useProvidersForDiagnostics } from "@/src/lib/data-fetchers/diagnosis-providers-fetchers";
import { fileViewUrl } from "@/src/lib/files/file-view-url";
import { formatDateForFilename, formatDateMmDdYyyy } from "@/src/lib/utils/date-and-phone-utils";
import {
  type WebecwTelephoneEncounterIdentity,
  WebecwTelEncNonCreationReason,
} from "@/src/lib/workflows/steps/webecw/tel-enc-contract";
import { V2WorkflowType } from "@/src/models/constants/workflow-types";
import { isStatOrUrgentPriority, Referral } from "@/src/models/documents/referral";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import { Task, TaskSource, TaskStatus } from "@/src/models/task";

const AUTONOMY_PRACTITIONER_NPI_BY_EHR_ID: Record<string, string> = Object.fromEntries(
  Object.entries(AUTONOMY_PRACTITIONER_NPIS).map(([npi, info]) => [info.ehrId, npi])
);

interface AutonomyReferralInstructionsProps {
  referral: Referral;
  task: Task;
  faxSenderNumber?: string | null;
  presentation: AutonomyReferralInstructionsPresentation;
}

type AutonomyReferralEhrPresentation = Exclude<
  AutonomyReferralInstructionsPresentation,
  { mode: V2WorkflowType.REFERRAL_EMAIL_ONLY_WF }
>;

type AutonomyReferralMessageProps = Omit<AutonomyReferralInstructionsProps, "presentation"> & {
  presentation: AutonomyReferralEhrPresentation;
};

const AUTONOMY_ECW_URL = "https://gacent96bbda5pqn16app.ecwcloud.com/mobiledoc/jsp/webemr/index.jsp";

const AUTONOMY_TE_NON_CREATION_FACTS: Record<WebecwTelEncNonCreationReason, string> = {
  [WebecwTelEncNonCreationReason.NOT_NEEDED]: "Telephone Encounter creation was not needed",
  [WebecwTelEncNonCreationReason.UNKNOWN_CHART_ORIGIN]: "Chart origin could not be confirmed",
  [WebecwTelEncNonCreationReason.EXISTING_TE]: "An existing Telephone Encounter was found",
  [WebecwTelEncNonCreationReason.OTHER_REFERRAL]: "Another referral for this patient holds the Telephone Encounter",
  [WebecwTelEncNonCreationReason.GUARD_UNAVAILABLE]: "Encounter history could not be confirmed",
  [WebecwTelEncNonCreationReason.UNRESOLVED_ASSIGNEE]: "Staff assignee could not be resolved",
  [WebecwTelEncNonCreationReason.UNRESOLVED_DOCTOR]: "Telephone Encounter doctor could not be resolved",
  [WebecwTelEncNonCreationReason.UNRESOLVED_FACILITY]: "Telephone Encounter facility could not be resolved",
  [WebecwTelEncNonCreationReason.EXPLICIT_CREATE_FAILURE]: "eCW reported that Telephone Encounter creation failed",
  [WebecwTelEncNonCreationReason.UNCONFIRMED_CREATE_RESULT]:
    "Telephone Encounter creation result could not be confirmed",
  [WebecwTelEncNonCreationReason.AUTHORIZATION_ON_EXISTING_TE]:
    "The referral authorization is on the patient's open Telephone Encounter for the PCP referral",
  [WebecwTelEncNonCreationReason.MULTIPLE_APPEND_TARGETS]:
    "More than one open Telephone Encounter mentions a PCP referral in Action Taken",
  [WebecwTelEncNonCreationReason.APPEND_UNCONFIRMED]:
    "The referral authorization could not be confirmed on the open Telephone Encounter for the PCP referral",
};

function findOutboundFaxFile(task: Task) {
  return task.files.find((file) => file.source === TaskSource.FAX_OUTBOUND);
}

function getProviderName(provider: Practitioner | null, fallback: string): string {
  if (provider) return getPractitionerNameWithCredentials(provider) || fallback;
  else return fallback;
}

function AssignmentNote() {
  return (
    <div className="text-xs text-muted-foreground italic">
      Note: Verify assignment based on patient&apos;s last encounter provider
    </div>
  );
}

function ManualUploadBoxes({
  fileName,
  assigneeName,
  showAssignmentNote,
}: {
  fileName: string;
  assigneeName: string;
  showAssignmentNote: boolean;
}) {
  return (
    <div className="space-y-2">
      <div>
        <span className="font-medium">Document Type:</span>{" "}
        <CopyableContent text={AUTONOMY_REFERRAL_MAPPING.displayName} />
      </div>
      <div>
        <span className="font-medium">ECW Folder:</span>{" "}
        <CopyableContent text={AUTONOMY_REFERRAL_MAPPING.folderMapping} />
      </div>
      <div>
        <span className="font-medium">File name:</span> <CopyableContent text={fileName} />
      </div>
      <div className="space-y-1">
        <div>
          <span className="font-medium">Assign to (receiving provider):</span> <CopyableContent text={assigneeName} />
        </div>
        {showAssignmentNote && <AssignmentNote />}
      </div>
      <div className="text-xs text-muted-foreground italic">
        {AUTONOMY_REFERRAL_MAPPING.markAsReviewed ? "Mark the document as reviewed." : "Leave the document open."}
      </div>
    </div>
  );
}

function ManualTelephoneEncounterBoxes({
  assigneeName,
  showAssignmentNote,
  reason,
  showReasonPrefixNote,
  message,
  highPriority,
}: {
  assigneeName: string;
  showAssignmentNote: boolean;
  reason: string;
  showReasonPrefixNote: boolean;
  message: string;
  highPriority: boolean;
}) {
  return (
    <div className="space-y-3">
      <div>
        <div className="font-medium">Assignment (receiving provider)</div>
        <div className="ml-2 text-sm space-y-1">
          <div>
            <CopyableContent text={assigneeName} />
          </div>
          {showAssignmentNote && <AssignmentNote />}
        </div>
      </div>

      <div>
        <div className="font-medium">Reason</div>
        <div className="ml-2 bg-muted/50 p-2 rounded">
          <CopyableContent text={reason} />
        </div>
        {showReasonPrefixNote && (
          <div className="ml-2 mt-1 text-xs text-muted-foreground italic">
            Add NP (New Patient) or EP (Established Patient) prefix based on encounter history
          </div>
        )}
      </div>

      <div>
        <div className="font-medium">Message</div>
        <div className="ml-2 bg-muted/50 p-3 rounded">
          <div className="text-sm whitespace-pre-wrap font-mono">{message}</div>
        </div>
      </div>

      {highPriority && (
        <div>
          <span className="font-medium">Priority:</span> High
        </div>
      )}
    </div>
  );
}

function getResolvedAssigneeName(assignment: AutonomyReferralAssignmentFact): string | null {
  return assignment.available && assignment.resolved ? assignment.name : null;
}

function getTelephoneEncounterBoxValues(
  draft: AutonomyReferralTelephoneEncounterDraftFact,
  fallbackReason: string,
  fallbackMessage: string
): { reason: string; message: string; showReasonPrefixNote: boolean } {
  return draft.available
    ? { reason: draft.reason, message: draft.message, showReasonPrefixNote: false }
    : { reason: fallbackReason, message: fallbackMessage, showReasonPrefixNote: true };
}

function getChartResultText(chart: AutonomyReferralChartResult): string {
  return chart.created ? "Patient chart created in eCW" : "Existing patient chart updated in eCW";
}

function AutonomyReferralProviderReview({ enrichment }: { enrichment: AutonomyReferralEnrichmentFact }) {
  if (!enrichment.available) return null;

  return (
    <>
      {enrichment.providerVerificationRequired && (
        <Alert className="mt-2">
          <AlertDescription className="text-sm">
            Provider enrichment requires review; verify the referring and rendering provider fields in eCW.
          </AlertDescription>
        </Alert>
      )}

      {enrichment.renderingMismatch && (
        <Alert className="mt-2">
          <AlertDescription className="text-sm">
            ⚠️ {formatAutonomyRenderingMismatch(enrichment.renderingMismatch)}
          </AlertDescription>
        </Alert>
      )}
    </>
  );
}

function AutonomyReferralUploadResult({
  assignment,
  upload,
}: {
  assignment: AutonomyReferralAssignmentFact;
  upload: AutonomyReferralUploadFact;
}) {
  if (!upload.available) return <>Document upload result unavailable</>;
  if (!assignment.available) return <>Document uploaded; assignment result unavailable</>;

  if (!assignment.resolved) {
    return (
      <>The document was uploaded without the intended Staff assignment; assign it to the correct reviewer in eCW.</>
    );
  }

  return (
    <>
      Uploaded and assigned to <CopyableContent text={assignment.name} />
    </>
  );
}

function AutonomyReferralEmailMessage({ email }: { email: AutonomyReferralEmailFact }) {
  const result = !email.available
    ? "Email forwarding result unavailable"
    : email.sent
      ? "Referral forwarded by email"
      : "Referral email was not sent — route the referral manually";

  return (
    <div className="flex flex-col gap-1">
      <p className="font-medium text-center mb-4">Autonomy Health Referral Instructions:</p>
      <div className="text-muted-foreground">{result}</div>
    </div>
  );
}

function TelephoneEncounterIdentityText({
  telephoneEncounter,
}: {
  telephoneEncounter: WebecwTelephoneEncounterIdentity;
}) {
  const date = formatDateMmDdYyyy(telephoneEncounter.date);

  return (
    <>
      {date ? ` dated ${date}` : ""}, with the Reason &ldquo;{telephoneEncounter.reason}&rdquo;
    </>
  );
}

function AuthorizationLineBox({ authorizationLine }: { authorizationLine: string | null }) {
  if (!authorizationLine) return null;

  return (
    <div className="bg-muted/50 p-2 rounded">
      <CopyableContent text={authorizationLine} />
    </div>
  );
}

function OtherReferralTelephoneEncounterAction({
  outcome,
  authorizationLine,
}: {
  outcome: AutonomyReferralOtherReferralOutcome;
  authorizationLine: string | null;
}) {
  const { otherReferral, telephoneEncounter } = outcome;
  const otherReferralLink = (
    <Link href={`/tasks/${otherReferral.displayId}`} className="font-medium text-foreground underline">
      {otherReferral.displayId}
    </Link>
  );

  return (
    <>
      {telephoneEncounter ? (
        <div>
          {otherReferralLink} has this patient&apos;s Telephone Encounter
          <TelephoneEncounterIdentityText telephoneEncounter={telephoneEncounter} />.
        </div>
      ) : (
        <div>
          {otherReferralLink} arrived first and was still being processed, so the Telephone Encounter is left to it.
        </div>
      )}
      <div>
        <span className="font-medium">Action:</span> Add anything new from this referral to that Telephone Encounter
        instead of creating another. If {otherReferralLink} has no Telephone Encounter, create one from its card.
      </div>
      {authorizationLine && (
        <>
          <div>
            Add this referral&apos;s authorization to that Telephone Encounter&apos;s Messages if it isn&apos;t there:
          </div>
          <AuthorizationLineBox authorizationLine={authorizationLine} />
        </>
      )}
    </>
  );
}

function AutonomyReferralTelephoneEncounterResult({
  telephoneEncounter,
  authorizationLine,
}: {
  telephoneEncounter: AutonomyReferralTelephoneEncounterFact;
  authorizationLine: string | null;
}) {
  if (!telephoneEncounter.available) return <>Telephone Encounter automation result is unavailable</>;
  if (telephoneEncounter.outcome.created) return <>eCW confirmed the Telephone Encounter was created</>;

  const outcome = telephoneEncounter.outcome;

  return (
    <div className="space-y-2">
      <div>{AUTONOMY_TE_NON_CREATION_FACTS[outcome.reason]}</div>
      {(outcome.reason === WebecwTelEncNonCreationReason.AUTHORIZATION_ON_EXISTING_TE ||
        outcome.reason === WebecwTelEncNonCreationReason.APPEND_UNCONFIRMED) && (
        <div>
          That is the Telephone Encounter
          <TelephoneEncounterIdentityText telephoneEncounter={outcome.telephoneEncounter} />.
        </div>
      )}
      {telephoneEncounter.reviewAction === WebecwTelEncNonCreationReason.OTHER_REFERRAL && (
        <OtherReferralTelephoneEncounterAction
          outcome={telephoneEncounter.outcome}
          authorizationLine={authorizationLine}
        />
      )}
      {telephoneEncounter.reviewAction === WebecwTelEncNonCreationReason.EXISTING_TE && (
        <div>
          <span className="font-medium">Action:</span> Check that Telephone Encounter before creating another.
        </div>
      )}
      {telephoneEncounter.reviewAction === WebecwTelEncNonCreationReason.UNCONFIRMED_CREATE_RESULT && (
        <div>
          <span className="font-medium">Action:</span> Verify in eCW whether the Telephone Encounter already exists
          before creating one.
        </div>
      )}
      {telephoneEncounter.reviewAction === WebecwTelEncNonCreationReason.MULTIPLE_APPEND_TARGETS && (
        <>
          <div>
            <span className="font-medium">Action:</span> Add this line to the Messages of the Telephone Encounter for
            the PCP referral, keeping its owner:
          </div>
          <AuthorizationLineBox authorizationLine={authorizationLine} />
        </>
      )}
      {telephoneEncounter.reviewAction === WebecwTelEncNonCreationReason.APPEND_UNCONFIRMED && (
        <>
          <div>
            <span className="font-medium">Action:</span> Check that Telephone Encounter&apos;s Messages and add this
            line if it isn&apos;t there, keeping its owner:
          </div>
          <AuthorizationLineBox authorizationLine={authorizationLine} />
        </>
      )}
    </div>
  );
}

function AutonomyReferralMessage({
  referral,
  task,
  faxSenderNumber = null,
  presentation,
}: AutonomyReferralMessageProps) {
  const providerAssignment =
    presentation.mode === V2WorkflowType.INBOUND_REFERRAL_WF && presentation.enrichment.available
      ? presentation.enrichment.providerAssignment
      : null;
  const teContext = buildTeReasonContext(referral, task, null, providerAssignment);
  const teReason = generateTeReasonText(teContext);
  const teMessage = generateTeMessage(referral, task, faxSenderNumber, providerAssignment);
  const missingInfo = getMissingInformationSummary(referral, task);
  const needsHmoReferral = requiresHmoReferral(referral);
  const isUrgent = isStatOrUrgentPriority(referral.schedulingPriority);
  const isSleepDiagnosis = hasSleepDiagnosis(referral);
  const isOptumServe = isOptumServeReferral(faxSenderNumber, referral);

  const { data: matchedProviders } = useProvidersForDiagnostics(referral.diagnosisList ?? null);

  const outboundFaxFile = findOutboundFaxFile(task);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fileName = `${formatDateForFilename(referral.dateReceived, "")} Referral from ${getProviderName(referral.referringProvider, "Unknown Provider")}`;

  const renderingProviderName =
    providerAssignment?.providerName ?? getProviderName(referral.receivingProvider, "Rendering Provider");

  const npi = providerAssignment?.providerNpi ?? referral.receivingProvider?.npiNumber ?? null;
  const supervisorLeadMa = (npi ? AUTONOMY_PRACTITIONER_MAPPINGS[npi] : null)?.supervisorLeadMa || null;
  const staffName = isSleepDiagnosis ? "Nicole Dunlap" : supervisorLeadMa;
  const manualAssigneeName = staffName ?? `${renderingProviderName} - Staff`;

  const matchedNpis = new Set(
    (matchedProviders ?? [])
      .map((p) => AUTONOMY_PRACTITIONER_NPI_BY_EHR_ID[p.ehrId])
      .filter((matched): matched is string => !!matched)
  );
  const hasProviderConflict =
    !isSleepDiagnosis && hasAutonomyDiagnosisRules(npi) && !!matchedProviders?.length && !matchedNpis.has(npi);
  const alternativeProviderNames = (matchedProviders ?? []).map((p) => p.name);

  return (
    <div className="flex flex-col gap-1">
      <p className="font-medium text-center mb-4">Autonomy Health Referral Instructions:</p>

      <div className="text-muted-foreground flex flex-col gap-2">
        {presentation.mode === V2WorkflowType.INBOUND_REFERRAL_WF ? (
          <>
            <div className="flex flex-col gap-2">
              <span className="font-semibold">
                1. {getChartResultText(presentation.chart)}:{" "}
                <a
                  href={AUTONOMY_ECW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:bg-accent rounded p-1"
                >
                  Link
                </a>
              </span>
              <div className="ml-4 space-y-1 text-xs">
                <div>Complete: Pharmacy, Additional Information, Responsible Party sections</div>
                <div>Run insurance eligibility if plan type is unclear</div>
              </div>
            </div>

            <AutonomyReferralProviderReview enrichment={presentation.enrichment} />

            <div className="flex flex-col gap-2">
              <span className="font-semibold">2. Document upload</span>
              <div className="ml-4 space-y-2">
                <AutonomyReferralUploadResult assignment={presentation.assignment} upload={presentation.upload} />
                {!presentation.upload.available && (
                  <>
                    <div>Check {AUTONOMY_REFERRAL_MAPPING.folderMapping} for this document before uploading it.</div>
                    <ManualUploadBoxes
                      fileName={presentation.documentLabel ?? fileName}
                      assigneeName={getResolvedAssigneeName(presentation.assignment) ?? manualAssigneeName}
                      showAssignmentNote={!getResolvedAssigneeName(presentation.assignment)}
                    />
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <span className="font-semibold">
                1. Create or Update the patient in eCW:{" "}
                <a
                  href={AUTONOMY_ECW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:bg-accent rounded p-1"
                >
                  Link
                </a>
              </span>
              <div className="ml-4 space-y-1 text-xs">
                <div>Complete: Pharmacy, Additional Information, Responsible Party sections</div>
                <div>Run insurance eligibility if plan type is unclear</div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="font-semibold">2. Upload the file to the proper folder</span>
              <div className="ml-4">
                <ManualUploadBoxes fileName={fileName} assigneeName={manualAssigneeName} showAssignmentNote />
              </div>
            </div>
          </>
        )}

        <div className="flex flex-col gap-2">
          <span className="font-semibold">3. Telephone Encounter</span>

          {presentation.mode === V2WorkflowType.INBOUND_REFERRAL_WF ? (
            <div className="ml-4 space-y-3">
              <AutonomyReferralTelephoneEncounterResult
                telephoneEncounter={presentation.telephoneEncounter}
                authorizationLine={presentation.authorizationLine}
              />
              {presentation.telephoneEncounterBoxes.show && (
                <ManualTelephoneEncounterBoxes
                  assigneeName={getResolvedAssigneeName(presentation.assignment) ?? manualAssigneeName}
                  showAssignmentNote={!getResolvedAssigneeName(presentation.assignment)}
                  highPriority={presentation.highPriority}
                  {...getTelephoneEncounterBoxValues(presentation.telephoneEncounterBoxes.draft, teReason, teMessage)}
                />
              )}
            </div>
          ) : (
            <div className="ml-4">
              <ManualTelephoneEncounterBoxes
                assigneeName={manualAssigneeName}
                showAssignmentNote
                reason={teReason}
                showReasonPrefixNote
                message={teMessage}
                highPriority={presentation.highPriority}
              />
            </div>
          )}
        </div>

        {isUrgent && (
          <Alert className="mt-2">
            <AlertDescription className="text-sm">
              ⚠️ Scheduling priority is {referral.schedulingPriority}. Mark the Telephone Encounter as High Priority in
              eCW.
            </AlertDescription>
          </Alert>
        )}

        {isSleepDiagnosis && (
          <Alert className="mt-2">
            <AlertDescription className="text-sm">
              Sleep-related diagnosis — Use Eslin Luka as rendering provider.
            </AlertDescription>
          </Alert>
        )}

        {isSleepDiagnosis && (
          <Alert className="mt-2">
            <AlertDescription className="text-sm">
              Sleep-related diagnosis — Assign referral document and Telephone Encounter to Nicole Dunlap.
            </AlertDescription>
          </Alert>
        )}

        {hasProviderConflict && (
          <Alert className="mt-2">
            <AlertDescription className="text-sm">
              ⚠️ {renderingProviderName} doesn&apos;t typically accept this diagnosis per the scheduling guide.
              Consider: {alternativeProviderNames.join(", ")}.
            </AlertDescription>
          </Alert>
        )}

        {needsHmoReferral && (
          <Alert className="mt-2">
            <AlertDescription className="text-sm">
              ⚠️ Patient Needs PCP/HMO Referral. Please contact referring provider.
            </AlertDescription>
          </Alert>
        )}

        {isOptumServe && (
          <Alert className="mt-2">
            <AlertDescription className="text-sm">
              Optum Serve referral; only Gincy Samuel, MD accepts these referrals
            </AlertDescription>
          </Alert>
        )}

        {missingInfo.length > 0 && (
          <Alert className="mt-2">
            <AlertDescription className="text-sm">
              <div>
                ℹ️ Requested the following - please check if they&apos;ve arrived before reaching out to the provider:
              </div>
              <ul className="list-disc list-inside space-y-1 mt-2">
                {missingInfo.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>

              {outboundFaxFile && (
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="mt-3">
                      View Outbound Fax
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl h-[80vh]">
                    <DialogHeader>
                      <DialogTitle>Outbound Fax - Missing Information Request</DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 overflow-hidden">
                      <PDFViewer url={fileViewUrl(outboundFaxFile.pathToFile)} />
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}

function AutonomyReferralCards({ referral }: Pick<AutonomyReferralInstructionsProps, "referral">) {
  return (
    <>
      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable />}
      {referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable />}
    </>
  );
}

function AutonomyReferralAutomationRunning() {
  return (
    <div className="flex flex-col gap-1">
      <p className="font-medium text-center mb-4">Autonomy Health Referral Instructions:</p>
      <div className="text-muted-foreground">
        Automation is still running for this referral. Refresh once it finishes.
      </div>
    </div>
  );
}

export function AutonomyReferralInstructions({
  referral,
  task,
  faxSenderNumber = null,
  presentation,
}: AutonomyReferralInstructionsProps) {
  const instructions =
    task.status === TaskStatus.PROCESSING ? (
      <AutonomyReferralAutomationRunning />
    ) : presentation.mode === V2WorkflowType.REFERRAL_EMAIL_ONLY_WF ? (
      <AutonomyReferralEmailMessage email={presentation.email} />
    ) : (
      <AutonomyReferralMessage
        referral={referral}
        task={task}
        faxSenderNumber={faxSenderNumber}
        presentation={presentation}
      />
    );

  return (
    <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">{instructions}</div>
      </EhrInstructionsCard>

      <AutonomyReferralCards referral={referral} />
    </div>
  );
}
